import React, { useEffect, useState } from 'react';
import { Download, Copy, Calculator, Clock } from 'lucide-react';
import { formatCurrency, formatDurationLong, formatDurationWithSeconds } from '../utils/formatters';
import { Comparisons } from '../utils/comparisons';
import { Storage } from '../utils/storage';

export default function ResultsPage({ meetingData, navigateTo, setHistory, currency }) {
  const [hasSaved, setHasSaved] = useState(false);
  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [linkedInText, setLinkedInText] = useState('');

  useEffect(() => {
    if (meetingData && !hasSaved) {
      Storage.saveMeeting(meetingData);
      setHistory(Storage.getHistory());
      setHasSaved(true);
    }
  }, [meetingData, setHistory, hasSaved]);

  if (!meetingData) {
    navigateTo('home');
    return null;
  }

  const displayCurrency = meetingData.currency || currency;
  const visualComps = meetingData.visualComparisons || Comparisons.getVisualComparisons(meetingData.totalCost);
  
  // Ensure we have valid participant count
  const participantCount = meetingData.participants || meetingData.participantDetails?.length || 1;
  const avgPerPerson = meetingData.totalCost / participantCount;

  const downloadShareImage = () => {
    try {
      const canvas = document.createElement('canvas');
      const hasComparisons = visualComps.length > 0;
      
      canvas.width = 1200;
      canvas.height = hasComparisons ? 800 : 500;
      const ctx = canvas.getContext('2d');

      const gradient = ctx.createLinearGradient(0, 0, 1200, canvas.height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 1200, canvas.height);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.beginPath();
      const boxHeight = hasComparisons ? 700 : 400;
      const x = 50, y = 50, w = 1100, h = boxHeight, r = 20;
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 48px -apple-system, system-ui, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Meeting Cost Report', 600, 120);

      ctx.fillStyle = '#7c3aed';
      ctx.font = 'bold 96px -apple-system, system-ui, Arial, sans-serif';
      ctx.fillText(formatCurrency(meetingData.totalCost, displayCurrency), 600, 230);

      ctx.fillStyle = '#4b5563';
      ctx.font = '32px -apple-system, system-ui, Arial, sans-serif';
      const duration = formatDurationLong(meetingData.durationSeconds || meetingData.duration * 60);
      ctx.fillText(`${duration} • ${participantCount} people`, 600, 280);

      if (hasComparisons) {
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(150, 320);
        ctx.lineTo(1050, 320);
        ctx.stroke();

        ctx.fillStyle = '#1f2937';
        ctx.font = 'bold 36px -apple-system, system-ui, Arial, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText("That's Equivalent To:", 150, 380);

        const itemsToShow = visualComps;
        let yPos = 450;
        let col = 0;
        
        itemsToShow.forEach((item, index) => {
          const xPos = col === 0 ? 150 : 600;
          
          ctx.font = '40px Arial, sans-serif';
          ctx.fillText(item.icon, xPos, yPos);
          
          ctx.fillStyle = '#7c3aed';
          ctx.font = 'bold 38px -apple-system, system-ui, Arial, sans-serif';
          ctx.fillText(item.count.toString(), xPos + 60, yPos);
          
          ctx.fillStyle = '#4b5563';
          ctx.font = '32px -apple-system, system-ui, Arial, sans-serif';
          ctx.fillText(item.name, xPos + 140, yPos);
          
          col++;
          if (col === 2) {
            col = 0;
            yPos += 70;
          }
        });

        ctx.fillStyle = '#9ca3af';
        ctx.font = '28px -apple-system, system-ui, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Calculate your meeting costs at meetingcostcalc.com', 600, 730);
      } else {
        ctx.fillStyle = '#9ca3af';
        ctx.font = '28px -apple-system, system-ui, Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Calculate your meeting costs at meetingcostcalc.com', 600, 430);
      }

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;
      link.download = `meeting-cost-${dateStr}-${timeStr}.png`;
      
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showNotification('✅ Image downloaded! Share it on social media.');
    } catch (error) {
      console.error('Error generating image:', error);
      showNotification('❌ Could not generate image. Try taking a screenshot instead.', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.textContent = message;
    const bgColor = type === 'success' ? '#10b981' : '#ef4444';
    notification.style.cssText = `position: fixed; top: 20px; right: 20px; background: ${bgColor}; color: white; padding: 16px 24px; border-radius: 8px; font-weight: 600; z-index: 9999; box-shadow: 0 4px 12px rgba(0,0,0,0.15);`;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transition = 'opacity 0.3s';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  };

  const copyText = () => {
    const visualItems = visualComps.map(item => `${item.icon} ${item.count} ${item.name}`).join(' • ');
    
    let text = `💸 Meeting Cost Report

Duration: ${formatDurationLong(meetingData.durationSeconds || meetingData.duration * 60)}
Total Cost: ${formatCurrency(meetingData.totalCost, displayCurrency)}
Participants: ${participantCount} people`;

    // Only add "For perspective" section if there are items
    if (visualComps.length > 0) {
      text += `

That's Equivalent To:
${visualItems}`;
    }

    // Add comparison if exists
    if (meetingData.comparison) {
      text += `

${meetingData.comparison}`;
    }

    text += `

Calculate your meeting costs at meetingcostcalc.com`;

    navigator.clipboard.writeText(text).then(() => {
      showNotification('✅ Copied to clipboard!');
    });
  };

const shareLinkedIn = () => {
    let shareText = `💸 Meeting Cost: ${formatCurrency(meetingData.totalCost, displayCurrency)} (${formatDurationWithSeconds(meetingData.duration)})`;
    
    if (visualComps.length > 0) {
      const visualItems = visualComps.slice(0, 3).map(item => `${item.icon} ${item.count} ${item.name}`).join(', ');
      shareText += `\n\nThat's equivalent to: ${visualItems}`;
    }
    
    if (meetingData.comparison) {
      shareText += `\n\n${meetingData.comparison}`;
    }
    
    shareText += `\n\nCalculate yours at meetingcostcalc.com`;
    
    // Copy to clipboard and show modal
    navigator.clipboard.writeText(shareText).then(() => {
      setLinkedInText(shareText);
      setShowLinkedInModal(true);
    }).catch(() => {
      setLinkedInText(shareText);
      setShowLinkedInModal(true);
    });
  };

  const openLinkedIn = () => {
    const url = window.location.href;
    setShowLinkedInModal(false);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=600');
  };

  const shareTwitter = () => {
    const url = window.location.href;
    let text = `Our ${formatDurationWithSeconds(meetingData.duration)} meeting = ${formatCurrency(meetingData.totalCost, displayCurrency)}`;
    
    // Only add "That's equivalent to" section if there are items
    if (visualComps.length > 0) {
      text += `\n\nThat's equivalent to: `;
      
      const urlPart = `\n\n💸 ${url}`;
      const availableChars = 280 - text.length - urlPart.length - 10;
      
      let itemsText = '';
      for (let i = 0; i < visualComps.length; i++) {
        const item = visualComps[i];
        const itemText = `${item.icon} ${item.count} ${item.name}`;
        const separator = i === 0 ? '' : ', ';
        const potentialText = itemsText + separator + itemText;
        
        if (potentialText.length > availableChars) {
          if (i < visualComps.length) {
            itemsText += '...';
          }
          break;
        }
        
        itemsText += separator + itemText;
      }
      
      text += itemsText;
    }
    
    text += `\n\n💸 ${url}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank', 'width=600,height=400');
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center text-gray-900 mb-8">Meeting Complete! 🎉</h1>

        <div className="text-center mb-8">
          <div className="text-sm text-gray-500 mb-2 uppercase tracking-wide font-semibold">Total Cost</div>
          <div className="text-6xl font-extrabold text-purple-600 mb-4">
            {formatCurrency(meetingData.totalCost, displayCurrency)}
          </div>
        </div>

        <div className="border-t border-b border-gray-200 py-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📊 Meeting Breakdown</h2>
          <div className="grid md:grid-cols-2 gap-4 text-gray-700">
            <div>Duration: <span className="font-semibold">{formatDurationLong(meetingData.durationSeconds || meetingData.duration * 60)}</span></div>
            <div>Participants: <span className="font-semibold">{participantCount} people</span></div>
            <div>Cost per minute: <span className="font-semibold">{formatCurrency(meetingData.costPerMinute, displayCurrency)}</span></div>
            <div>Average per person: <span className="font-semibold">{formatCurrency(avgPerPerson, displayCurrency)}</span></div>
          </div>
        </div>

        {visualComps.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">💡 That's Equivalent To:</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {visualComps.map((item, i) => (
                <div key={i} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 text-center border-2 border-purple-200">
                  <div className="text-5xl mb-2">{item.icon}</div>
                  <div className="text-3xl font-bold text-purple-600 mb-1">{item.count}</div>
                  <div className="text-sm text-gray-700 font-medium">{item.name}</div>
                  <div className="text-xs text-gray-500">{item.count} {item.unit}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {meetingData.comparison && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-8">
            <p className="text-xl font-bold text-gray-900 text-center">{meetingData.comparison}</p>
          </div>
        )}

        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📢 Share These Results</h2>
          
          <div className="mb-4">
            <button
              onClick={downloadShareImage}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:shadow-lg text-lg font-semibold"
            >
              <Download className="w-6 h-6" />
              Download Share Image
            </button>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mt-2">
              <p className="text-sm text-blue-800 text-center font-medium">
                📱 Perfect for social media - includes your meeting cost and visual comparisons
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={copyText}
              className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300"
            >
              <Copy className="w-6 h-6" />
              <span className="text-sm font-medium">Copy</span>
            </button>
            <button
              onClick={shareLinkedIn}
              className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-blue-700 text-white rounded-lg hover:bg-blue-800"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span className="text-sm font-medium">LinkedIn</span>
            </button>
            <button
              onClick={shareTwitter}
              className="flex flex-col items-center justify-center gap-2 px-4 py-4 bg-black text-white rounded-lg hover:bg-gray-800"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="text-sm font-medium">X</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              // Check if current meeting was Fixed Duration, Quick Mode, or Custom Mode
              const isFixedDuration = meetingData.participantDetails?.length > 0 && meetingData.participantDetails[0].isFixedDuration;
              const isQuickMode = meetingData.participantDetails?.length > 0 && meetingData.participantDetails[0].isQuickMode;
              
              if (isFixedDuration) {
                navigateTo('fixed-duration');
              } else if (isQuickMode) {
                navigateTo('quick-setup');
              } else {
                navigateTo('setup');
              }
            }}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg"
          >
            <Calculator className="w-5 h-5" />
            Calculate Another
          </button>
          <button
            onClick={() => navigateTo('history')}
            className="flex items-center justify-center gap-2 border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50"
          >
            <Clock className="w-5 h-5" />
            View History
          </button>
        </div>
        
        <button
          onClick={() => navigateTo('home')}
          className="w-full mt-4 text-gray-600 px-6 py-3 rounded-lg hover:bg-gray-100"
        >
          ← Back to Homepage
        </button>
        
      </div>
      
 {/* LinkedIn Share Instructions Modal */}
      {showLinkedInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">📋 Share on LinkedIn</h3>
            
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-4">
              <p className="text-green-800 font-semibold text-center">
                ✅ Post text copied to clipboard!
              </p>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 font-semibold mb-2">Next steps:</p>
              <ol className="list-decimal list-inside space-y-2 text-gray-700">
                <li>Click "Open LinkedIn" below</li>
                <li>Click in the LinkedIn post text area</li>
                <li>Paste (Ctrl+V on Windows, Cmd+V on Mac)</li>
                <li>Your meeting cost text will appear! 🎉</li>
              </ol>
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 <strong>Tip:</strong> You can also share the downloadable image for more visual impact!
                </p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3 mb-6 max-h-32 overflow-y-auto">
              <p className="text-sm text-gray-600 whitespace-pre-line">{linkedInText}</p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowLinkedInModal(false)}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={openLinkedIn}
                className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800"
              >
                Open LinkedIn
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}